import { useCallback, useState } from 'react';

interface UseHomeRotationProps {
  homePlacementRotation: number;
  setHomePlacementRotation: (rotation: number) => void;
  rotationSensitivity?: number; // 1.0 is default, higher values = more sensitive
}

export const useHomeRotation = ({
  homePlacementRotation,
  setHomePlacementRotation,
  rotationSensitivity = 1.0,
}: UseHomeRotationProps) => {
  const [rotatingHomePlacement, setRotatingHomePlacement] = useState(false);
  const [initialRotation, setInitialRotation] = useState(0);
  const [
    homePlacementRotationStartingMousePosition,
    setHomePlacementRotationStartingMousePosition,
  ] = useState([0, 0, 0] as [number, number, number]);

  const handleRotateStart = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setRotatingHomePlacement(true);
      setInitialRotation(homePlacementRotation);
      const rect = e.currentTarget.getBoundingClientRect();
      const homeCenter = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
      const initialAngle =
        Math.atan2(e.clientY - homeCenter.y, e.clientX - homeCenter.x) *
        (180 / Math.PI);
      setHomePlacementRotationStartingMousePosition([
        initialAngle,
        homeCenter.x,
        homeCenter.y,
      ]);
    },
    [homePlacementRotation]
  );

  const handleRotateMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!rotatingHomePlacement) return;
      const [initialAngle, centerX, centerY] =
        homePlacementRotationStartingMousePosition;
      const currentAngle =
        Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
      const deltaAngle = (currentAngle - initialAngle) * rotationSensitivity;
      setHomePlacementRotation(initialRotation + deltaAngle);
    },
    [
      homePlacementRotationStartingMousePosition,
      initialRotation,
      rotatingHomePlacement,
      rotationSensitivity,
      setHomePlacementRotation,
    ]
  );

  const handleRotateEnd = useCallback(() => {
    setRotatingHomePlacement(false);
  }, []);

  return {
    rotatingHomePlacement,
    handleRotateStart,
    handleRotateMove,
    handleRotateEnd,
  };
};
